import React, { useEffect, useRef } from 'react';
import { BREAKPOINTS } from 'radiance-ui/lib/constants';
import { Icon, IconName, IconSize } from '@curology/ui-components-web/react';

import {
  DesktopExpandContent,
  DesktopImageContainer,
  ExpandRow,
  ExpandRowContent,
  MobileExpandContent,
  MobileImageContainer,
  StepBody,
  StepContainer,
  StepHeader,
  StepNumber,
  iconStyles,
} from './Step.style';

export interface StepProps {
  index?: number;
  header: string;
  body?: React.ReactNode;
  desktopImage: JSX.Element;
  mobileImage: JSX.Element;
  isActive?: boolean;
  setIsActive?: (isActive: boolean) => void;
}

export const Step = ({
  index = 0,
  header,
  body,
  mobileImage,
  desktopImage,
  isActive = false,
  setIsActive = () => undefined,
}: StepProps) => {
  const hasMounted = useRef(false);
  const element = useRef<HTMLLIElement>(null);

  useEffect(() => {
    // This prevents scrolling to a default active step on page load
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (!isActive || window.innerWidth >= BREAKPOINTS.md) {
      return;
    }

    setTimeout(() => {
      element.current?.scrollIntoView({
        behavior: 'smooth',
      });
    });
  }, [isActive]);

  const handleOnClick = () => {
    setIsActive(true);
  };

  const handleMouseEnter = () => {
    if (window.innerWidth < BREAKPOINTS.md) return;

    setIsActive(true);
  };

  return (
    <StepContainer active={isActive} ref={element}>
      <ExpandRow onClick={handleOnClick} onMouseEnter={handleMouseEnter}>
        <DesktopImageContainer active={isActive}>
          {desktopImage}
        </DesktopImageContainer>
        <ExpandRowContent>
          <StepNumber>{index + 1}</StepNumber>
          <StepHeader>{header}</StepHeader>
          <Icon
            css={iconStyles}
            size={IconSize.Medium}
            name={IconName.AccordionCaretDown}
          />
          <DesktopExpandContent active={isActive}>
            <StepBody>{body}</StepBody>
          </DesktopExpandContent>
        </ExpandRowContent>
      </ExpandRow>
      <MobileExpandContent active={isActive}>
        <StepBody>{body}</StepBody>
        <MobileImageContainer>{mobileImage}</MobileImageContainer>
      </MobileExpandContent>
    </StepContainer>
  );
};
